(function (scope) {
  "use strict";
  const MAX = 5,
    MANUAL_MAX = 3;
  const modes = ["self", "sets", "guide", "surprise"];
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const list = (value) =>
    (Array.isArray(value) ? value : String(value || "").split(/[,·]/))
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
  const cents = (value) =>
    Number.isSafeInteger(Number(value)) && Number(value) >= 0
      ? Number(value)
      : null;
  function choices(response) {
    const result = [];
    for (const type of response?.sampleTypes || []) {
      const prices = new Set(
        Object.values(response.byProductId || {})
          .filter(
            (s) =>
              s.selectable && s.availableForCurated !== false &&
              s.sampleTypeId === type.id &&
              cents(s.sampleSurchargeCents) > 0,
          )
          .map((s) => Number(s.sampleSurchargeCents)),
      );
      for (const price of [...prices].sort((a, b) => a - b))
        result.push({
          id: type.id + "@" + price,
          typeId: type.id,
          name: type.name,
          surchargeCents: price,
        });
    }
    return result;
  }
  function price(selection, response, partial = false) {
    if (!selection || !modes.includes(selection.type) || !response) return null;
    let surcharge = 0,
      count = 0,
      counts = {},
      selectedChoices = [];
    const personal = ["guide", "surprise"].includes(selection.type);
    if (personal) {
      const offered = choices(response),
        requested = selection.profile?.types || {};
      for (const [id, quantity] of Object.entries(requested)) {
        if (!Number.isSafeInteger(quantity) || quantity < 0) return null;
        if (!quantity) continue;
        const choice = offered.find((c) => c.id === id);
        if (!choice) return null;
        count += quantity;
        surcharge += quantity * choice.surchargeCents;
        counts[choice.typeId] = (counts[choice.typeId] || 0) + quantity;
        selectedChoices.push({
          sampleTypeId: choice.typeId,
          name: choice.name,
          surchargeCents: choice.surchargeCents,
          quantity,
        });
      }
      if (count > MANUAL_MAX) return null;
    } else {
      const ids = selection.ids || [];
      if (
        (!partial && ids.length !== MAX) ||
        ids.length > MAX ||
        new Set(ids).size !== ids.length
      )
        return null;
      for (const id of ids) {
        const item = response.byProductId?.[id];
        if (!item?.selectable || cents(item.sampleSurchargeCents) === null)
          return null;
        const extra = Number(item.sampleSurchargeCents);
        surcharge += extra;
        if (extra > 0) {
          count++;
          counts[item.sampleTypeId] = (counts[item.sampleTypeId] || 0) + 1;
        }
      }
    }
    const variant = response.pricing?.variants?.find(
      (v) =>
        Number(v.surchargeCents) === surcharge &&
        v.id &&
        v.available !== false &&
        cents(v.price) !== null,
    );
    if (!variant) return null;
    return {
      variantId: String(variant.id),
      price: Number(variant.price),
      surcharge,
      count,
      counts,
      choices: selectedChoices,
    };
  }
  function typeSummary(profile, response, money) {
    return choices(response)
      .filter((c) => profile?.types?.[c.id] > 0)
      .map(
        (c) =>
          profile.types[c.id] +
          " × " +
          c.name +
          " (+" +
          money(c.surchargeCents) +
          ")",
      )
      .join(", ");
  }
  function properties(selection, response, catalog, money) {
    const value = price(selection, response);
    if (!value) return null;
    const personal = ["guide", "surprise"].includes(selection.type);
    const names = (selection.ids || []).map(
      (id) => catalog.find((p) => p.id === String(id))?.name,
    );
    if (!personal && names.some((name) => !name)) return null;
    const prefs = [];
    if (selection.type === "guide") {
      const intensities = list(selection.profile?.intensity);
      if (!selection.profile?.families?.length || !intensities.length)
        return null;
      prefs.push(
        "Geurfamilies: " + selection.profile.families.join(", "),
        "Intensiteit: " + intensities.join(", "),
      );
    }
    if (personal) {
      prefs.push(
        "Sampletypes: " +
          (typeSummary(selection.profile, response, money) || "Standaard"),
      );
      if (selection.profile?.notes?.trim())
        prefs.push("Notities: " + selection.profile.notes.trim());
    }
    const fields = {
      "Sample service optie": {
        self: "Zelf samenstellen",
        sets: "Complete sets",
        guide: "Door Ornata",
        surprise: "Verras me",
      }[selection.type],
      "Sample service selectie": personal
        ? ""
        : selection.type === "sets"
          ? (selection.customSet ? "Custom Set: " : "Set: ") +
            selection.name +
            " (" +
            names.join(", ") +
            ")"
          : names.join(", "),
      "Sample service voorkeuren": prefs.join(" | "),
      _sample_ids: personal ? "" : selection.ids.join(", "),
      _sample_names: personal ? "" : JSON.stringify(names),
      _sample_type_counts: JSON.stringify(value.counts),
      _sample_price_choices: personal ? JSON.stringify(value.choices) : "",
      _sample_surcharge_cents: String(value.surcharge),
    };
    if (value.count) {
      fields["Aantal premium samples"] = String(value.count);
      fields._premium_sample_count = String(value.count);
    }
    return { ...value, properties: fields };
  }
  function sanitize(value) {
    if (!value || !modes.includes(value.type)) return null;
    const ids = Array.isArray(value.ids)
      ? value.ids.map(String).filter((id) => /^[1-9]\d{0,19}$/.test(id))
      : [];
    if (ids.length > MAX || new Set(ids).size !== ids.length) return null;
    const types = {};
    for (const [key, n] of Object.entries(value.profile?.types || {}))
      if (
        Number.isSafeInteger(n) &&
        n >= 0 &&
        n <= MANUAL_MAX &&
        key.length < 150
      )
        types[key] = n;
    return {
      type: value.type,
      ids,
      slotIds:
        Array.isArray(value.slotIds) && value.slotIds.length === MAX
          ? value.slotIds.map((id) =>
              ids.includes(String(id)) ? String(id) : null,
            )
          : Array.from({ length: MAX }, (_, i) => ids[i] || null),
      name: String(value.name || "").slice(0, 160),
      selectedSet: String(value.selectedSet || ""),
      customSet: value.customSet === true,
      profile: {
        families: list(value.profile?.families).slice(0, 3),
        intensity: [...new Set(list(value.profile?.intensity))].slice(0, 3).map((item) => item.slice(0, 80)),
        notes: String(value.profile?.notes || "").slice(0, 500),
        types,
      },
    };
  }
  const api = {
    MAX,
    MANUAL_MAX,
    clone,
    list,
    choices,
    price,
    properties,
    typeSummary,
    sanitize,
  };
  if (typeof module === "object" && module.exports) module.exports = api;
  else scope.OrnataSampleAtelierData = api;
})(typeof window === "undefined" ? globalThis : window);



